import { generate } from "shortid";
import { geohashForLocation } from "geofire-common";
import {
  AccountData,
  Event,
  EventType,
  GoogleLocationData,
  validExternalLinkUrls,
} from "@markit/common.types";
import {
  deepCopy,
  extractUniqueFormQuestionIdsFromCustomTickets,
  isProductSpecificType,
  makeEmptyEventData,
  getCanonicalMediaUrls,
  getEventBaseStoragePath,
  getProductBaseStoragePath,
  isFirebaseStorageUrl,
} from "@markit/common.utils";
import axios from "axios";
import { firebaseMapsApiKey } from "../firebase";
import { API } from "../API";
import merge from "lodash.merge";
import {
  saveMediaToFirestore,
  storeEventImageIntoFirebaseStorage,
} from "./photoUtils";
import {
  buildFinalMediaUrls,
  deleteRemovedCustomTicketMediaFromStorage,
  deleteRemovedMediaFromStorage,
} from "./mediaUtils";

// Move to bit
export const DEFAULT_TICKET_PRICE = 5 * 100;
export const DEFAULT_ORDER_PRICE = 10 * 100;
export const DEFAULT_CROWDFUNDING_GOAL = 100 * 100;
export const DEFAULT_GOING_CAPACITY = 100;
export const DEFAULT_CUSTOM_TICKET_GOING_CAPACITY = 10;
export const MIN_TICKET_QUANTITY = 1;
export const MAX_TICKET_QUANTITY = 24;

export const MAX_TICKET_PRICE = 2000 * 100; // $2000 in cents

export const makeEmptyEvent = (
  userId: string,
  isAdmin: boolean,
  currentLocation?: GoogleLocationData,
  type?: EventType
) => {
  const [lat, lng] =
    currentLocation && currentLocation.latitude !== -999
      ? [currentLocation.latitude, currentLocation.longitude]
      : [-1, -1];
  const eventId = generate();
  const customTicketId = generate();
  return makeEmptyEventData(
    eventId,
    userId,
    isAdmin,
    customTicketId,
    geohashForLocation([lat, lng]),
    currentLocation,
    type
  );
};

export const makeDuplicateEvent = (
  userData: AccountData,
  event: Event,
  eventDetailsChecked: boolean
) => {
  const emptyEvent = makeEmptyEvent(userData.uid, userData.isAdmin);

  if (!eventDetailsChecked) {
    return emptyEvent;
  }

  const eventFormQuestions =
    extractUniqueFormQuestionIdsFromCustomTickets(event);

  const mediaUrls = getCanonicalMediaUrls(event);
  const newEvent = merge(emptyEvent, {
    body: event.body,
    canRefund: event.canRefund,
    canTransfer: event.canTransfer,
    categories: event.categories,
    crowdfundingGoal: event.crowdfundingGoal,
    crowdfundingHide: event.crowdfundingHide,
    customTickets: event.customTickets,
    description: event.description,
    formattedAddress: event.formattedAddress,
    city: event.city,
    state: event.state,
    googleCalendarInvites: event.googleCalendarInvites,
    hideResponses: event.hideResponses,
    lat: event.lat,
    link: event.link,
    lng: event.lng,
    locationDetails: event.locationDetails,
    geohash: event.geohash,
    googleDescription: event.googleDescription,
    googlePlaceId: event.googlePlaceId,
    photoURL: isProductSpecificType(event.eventType) ? event.photoURL : "", // Keep photoURL for products (social preview), clear for events since old field
    privateLocation: event.privateLocation,
    isDraft: event.isDraft,
    isVirtual: event.isVirtual,
    isManualSoldOut: event.isManualSoldOut,
    title: event.title,
    timezone: event.timezone,
    tippingEnabled: event.tippingEnabled,
    universityId: event.universityId,
    visibility: event.visibility,
    theme: event.theme,
    formQuestions: eventFormQuestions,
    eventType: event.eventType,
    accessLevel: event.accessLevel,
    mediaURLs: mediaUrls,
    mediaAspectRatio: event.mediaAspectRatio,
    mediaDownloadable: event.mediaDownloadable,
  });

  return newEvent;
};

export const invalidEndDate = (event: Event) => {
  return new Date(event.end).getTime() <= new Date(event.start).getTime();
};

// Uploads the blob to firebase storage
// For products, we handle storing social preview image to photoURL
// For all else, we handle storing the image file(s) to mediaURLs
// For both, custom ticket mediaURL images are uploaded to storage
export const uploadBlobToFirebase = async (
  event: Event,
  isCreated: boolean
): Promise<Event> => {
  // some extra checks to mutate final event object
  const finalEvent = finalizeEvent(event, isCreated);
  const eventCopy: Event = deepCopy(finalEvent);

  // Products keep existing single social preview upload behavior (uses photoURL)
  // The logic for saving the product mediaURLs is in CreateProduct.tsx
  if (isProductSpecificType(event.eventType)) {
    if (event.photoURL && !isFirebaseStorageUrl(event.photoURL)) {
      try {
        const newPhotoUrl = await storeEventImageIntoFirebaseStorage(
          event,
          event.photoURL
        );
        eventCopy.photoURL = newPhotoUrl;
      } catch (err: any) {
        console.log("Error saving product preview photo: " + err.message);
      }
    }
  } else {
    // For events, we store onto mediaURLs
    const mediaUrls = getCanonicalMediaUrls(eventCopy);
    const finalMediaUrls = await buildFinalMediaUrls(mediaUrls, async (url) => {
      const uploaded = await storeEventImageIntoFirebaseStorage(eventCopy, url);
      return uploaded ?? null;
    });

    if (!isCreated) {
      await deleteRemovedMediaFromStorage(event, finalMediaUrls, (url) => {
        const decoded = decodeURIComponent(url);
        return decoded.includes(
          getEventBaseStoragePath(event.createdBy, event.id)
        );
      });
    }

    eventCopy.mediaURLs = finalMediaUrls;
  }

  // Upload custom ticket mediaURL images for both events and products
  eventCopy.customTickets = await Promise.all(
    eventCopy.customTickets.map(async (ticket) => {
      if (!ticket.mediaURL || isFirebaseStorageUrl(ticket.mediaURL)) {
        return ticket;
      }
      try {
        const basePath = isProductSpecificType(event.eventType)
          ? getProductBaseStoragePath(event.createdBy, event.id)
          : getEventBaseStoragePath(event.createdBy, event.id);
        const uploadedUrl = await saveMediaToFirestore(
          ticket.mediaURL,
          `${basePath}/${ticket.id}`,
          ""
        );
        return { ...ticket, mediaURL: uploadedUrl };
      } catch (err: any) {
        console.log("Error saving ticket image: " + err.message);
        return ticket;
      }
    })
  );

  if (!isCreated) {
    await deleteRemovedCustomTicketMediaFromStorage(
      event,
      eventCopy.customTickets
    );
  }

  return eventCopy;
};

export const finalizeEvent = (event: Event, isCreated?: boolean) => {
  return {
    ...event,
    ...(isCreated && { createdAt: new Date().toISOString() }),
    title: event.title !== "" ? event.title : "",
  } as Event;
};

export const getFormattedAddress = async (googlePlaceId: string) => {
  try {
    const url =
      "https://maps.googleapis.com/maps/api/place/details/json?place_id=" +
      googlePlaceId +
      "&key=" +
      firebaseMapsApiKey;
    const response = await axios.get(url);
    return response.data.result?.formatted_address as string;
  } catch (err: any) {
    console.error(err.message);
  }
};

export const getDescriptionFromPlaceId = async (googlePlaceId: string) => {
  try {
    const response = await API.tracking.getEventGoogleDescription({
      placeId: googlePlaceId,
    });
    if (response) {
      return response.googleDescription;
    }
  } catch (err: any) {
    console.error(err.message);
  }
};

export const getCityStateFromAddressComponents = (
  addressComponents: any[]
): { city: string; state: string } => {
  const cityComponent = addressComponents.find((component) =>
    component.types.includes("locality")
  );
  const stateComponent = addressComponents.find((component) =>
    component.types.includes("administrative_area_level_1")
  );
  return {
    city: cityComponent?.long_name ?? "",
    state: stateComponent?.short_name ?? "",
  };
};

export const createExternalEventLink = async (
  eventLink: string,
  userData: AccountData,
  eventToConvert?: Event // If converting to markit event, pass this in
): Promise<{ event: Event | undefined; dataScraped: boolean }> => {
  const trimmedLink = eventLink.trim();
  const newEvent: Event = {
    ...makeEmptyEvent(userData.uid, userData.isAdmin, undefined),
    externalLink: trimmedLink,
    eventType: EventType.EXTERNAL_NO_DATA, // Default to no data collection, collect data type only within public api
  };
  // Supported external link (scrape details)
  const isSupported = validExternalLinkUrls.some((validUrl) =>
    trimmedLink.includes(validUrl.url)
  );
  if (isSupported) {
    const fetchExternalEvent = trimmedLink.includes(
      validExternalLinkUrls[0].url
    )
      ? API.eventScraper.fetchEventbriteEvent
      : trimmedLink.includes(validExternalLinkUrls[1].url)
        ? API.eventScraper.fetchPoshEvent
        : trimmedLink.includes(validExternalLinkUrls[2].url)
          ? API.eventScraper.fetchLumaEvent
          : trimmedLink.includes(validExternalLinkUrls[3].url)
            ? API.eventScraper.fetchPartifulEvent
            : API.eventScraper.fetchMarkitEvent;
    const response = await fetchExternalEvent({
      url: trimmedLink,
      userId: userData.uid,
      isGenericLink: false,
      eventDataToConvert: eventToConvert,
    });
    const { eventDetails, success } = response;
    if (!success) {
      return { event: newEvent, dataScraped: false };
    }

    return { event: eventDetails, dataScraped: true };
  } else {
    // Don't scrape details
    return { event: newEvent, dataScraped: false };
  }
};
